import React, { useEffect, useMemo, useState } from "react";
import { AutoCompleteComponent } from "@syncfusion/ej2-react-dropdowns";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { get } from "lodash";
import useGetAllCnaeEmployee from "services/utils/useGetAllCnaeEmployee.";
import { getFechaCNAE } from "services/utils/fechaCNAEUtils";

interface IInputProps {
  name: string;
  descriptionName?: string;
  idName?: string;
  required?: boolean | string;
  disabled?: boolean;
  xl?: number;
  md?: number;
  col?: number;
  label?: string;
  placeholder?: string;
}

interface CnaeOption {
  Id: string;
  Codigo: string;
  Descripcion: string;
}

type SyncfusionDataSourceItem = { [key: string]: Object };

const normalizeCode = (value: unknown): string => String(value ?? "").trim();

const isDigitsOnly = (value: string): boolean => /^[0-9]+$/.test(value);

const areCodesEquivalent = (left: unknown, right: unknown): boolean => {
  const a = normalizeCode(left);
  const b = normalizeCode(right);

  if (!a || !b) return false;
  if (a === b) return true;

  if (isDigitsOnly(a) && isDigitsOnly(b)) {
    return String(Number(a)) === String(Number(b));
  }

  return false;
};

const getValueByKeys = (source: Record<string, unknown>, keys: string[]): unknown => {
  const normalized = Object.entries(source).map(([k, v]) => [
    k.replace(/[_\s]/g, "").toLowerCase(),
    v,
  ] as const);

  for (const key of keys) {
    const target = key.replace(/[_\s]/g, "").toLowerCase();
    const match = normalized.find(([k]) => k === target);
    if (match) return match[1];
  }

  return undefined;
};

const extractRows = (response: unknown): Record<string, unknown>[] => {
  if (Array.isArray(response)) return response as Record<string, unknown>[];
  if (!response || typeof response !== "object") return [];

  const src = response as Record<string, unknown>;
  const candidates = [src.items, src.Items, src.result, src.Result, src.data, src.Data];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as Record<string, unknown>[];
  }

  return [];
};

const FormInputCNAE: React.FC<IInputProps> = ({
  name,
  descriptionName,
  idName,
  required,
  disabled = false,
  xl = 12,
  md = 12,
  col = 12,
  label,
  placeholder,
}) => {
  const idCode = name;
  const idDesc = descriptionName ?? `${name}Descripcion`;
  const idSelected = idName ?? `${name}Id`;

  const { getAllCnaeEmployee, watchFechaCNAE } = useGetAllCnaeEmployee();
  const {
    register,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  const [allData, setAllData] = useState<CnaeOption[]>([]);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [autoCompleteRefreshKey, setAutoCompleteRefreshKey] = useState(0);

  const currentCode = useWatch({ control, name: idCode, defaultValue: getValues(idCode) });
  const currentDesc = useWatch({ control, name: idDesc, defaultValue: getValues(idDesc) });

  const cnaeData = useMemo(() => allData, [allData]);

  const setIfChanged = (field: string, value: any) => {
    const current = getValues(field);
    if (current !== value) {
      setValue(field, value);
    }
  };

  useEffect(() => {
    register(idCode);
    register(idSelected);
  }, [idCode, idSelected, register]);

  useEffect(() => {
    let isMounted = true;

    const loadCatalog = async (fecha: string) => {
      const res = await getAllCnaeEmployee(fecha || "");
      const rows = extractRows(res);
      const mappedData: CnaeOption[] = rows
        .map((row) => {
          const rawId = getValueByKeys(row, ["id", "Id"]);
          const rawCode = getValueByKeys(row, ["codigo", "Codigo", "code"]);
          const rawDesc = getValueByKeys(row, ["descripcion", "Descripcion", "description"]);

          return {
            Id: String(rawId ?? ""),
            Codigo: String(rawCode ?? "").trim(),
            Descripcion: String(rawDesc ?? "").trim(),
          };
        })
        .filter((item) => item.Codigo !== "" && item.Descripcion !== "");

      if (isMounted) {
        setAllData(mappedData);
        setCatalogLoaded(true);
      }
    };

    loadCatalog(getFechaCNAE());

    const stopWatch = watchFechaCNAE((fecha: string) => {
      loadCatalog(fecha);
    });

    return () => {
      isMounted = false;
      stopWatch && stopWatch();
    };
  }, []);

  useEffect(() => {
    if (!catalogLoaded) return;

    const codeFromForm = normalizeCode(currentCode ?? getValues(idCode));
    const descFromForm = String(currentDesc ?? getValues(idDesc) ?? "");

    if (!cnaeData.length) {
      if (!codeFromForm && !descFromForm) {
        setIfChanged(idSelected, "");
      }
      return;
    }

    if (codeFromForm) {
      const selectedByCode = cnaeData.find((item) => areCodesEquivalent(item.Codigo, codeFromForm));
      if (selectedByCode) {
        if (descFromForm !== selectedByCode.Descripcion) {
          setIfChanged(idDesc, selectedByCode.Descripcion);
          setAutoCompleteRefreshKey((prev) => prev + 1);
        }
        if (!areCodesEquivalent(codeFromForm, selectedByCode.Codigo)) {
          setIfChanged(idCode, selectedByCode.Codigo);
        }
        setIfChanged(idSelected, selectedByCode.Id);
        return;
      }
    }

    if (descFromForm) {
      const selectedByDesc = cnaeData.find((item) => item.Descripcion === descFromForm);
      if (selectedByDesc) {
        if (!areCodesEquivalent(codeFromForm, selectedByDesc.Codigo)) {
          setIfChanged(idCode, selectedByDesc.Codigo);
        }
        setIfChanged(idSelected, selectedByDesc.Id);
        return;
      }
    }

    if (!codeFromForm && !descFromForm) {
      setIfChanged(idSelected, "");
    }
  }, [catalogLoaded, cnaeData, currentCode, currentDesc, idCode, idDesc, idSelected]);

  const handleCodeChange = (rawValue: string) => {
    const code = normalizeCode(rawValue);
    setIfChanged(idCode, code);

    if (!code) {
      setIfChanged(idDesc, "");
      setIfChanged(idSelected, "");
      return;
    }

    const selectedByCode = cnaeData.find((item) => areCodesEquivalent(item.Codigo, code));
    if (selectedByCode) {
      setIfChanged(idCode, selectedByCode.Codigo);
      setIfChanged(idDesc, selectedByCode.Descripcion);
      setIfChanged(idSelected, selectedByCode.Id);
      setAutoCompleteRefreshKey((prev) => prev + 1);
      return;
    }

    setIfChanged(idDesc, "");
    setIfChanged(idSelected, "");
  };

  const errorMessage = (get(errors, idCode)?.message || get(errors, idDesc)?.message) as string | undefined;

  return (
    <div className={`col-xl-${xl} col-md-${md} col-${col}`}>
      <fieldset className={`input-block input-block--code ${errorMessage ? "error" : ""}`}>
        <div className="container-leyend">
          <legend className="input-block--code__legend">{label ?? "CNAE para cotización AT/EP"}</legend>
        </div>

        <div className="row">
          <div className="col-sm-12 col-md-3 col-lg-3">
            <input
              type="text"
              name={idCode}
              id={idCode}
              value={String(currentCode ?? "")}
              maxLength={4}
              minLength={4}
              onChange={(e) => handleCodeChange(e.target.value)}
              disabled={disabled}
            />
          </div>

          <div className="col-sm-12 col-md-9 delta-autocomplete" data-input-name={idDesc}>
            <Controller
              name={idDesc}
              control={control}
              defaultValue={getValues(idDesc) || ""}
              rules={{
                required:
                  required && typeof required === "string"
                    ? required
                    : required
                      ? "Este campo es obligatorio"
                      : false,
              }}
              render={({ field }) => (
                <AutoCompleteComponent
                  key={`cnae-${autoCompleteRefreshKey}`}
                  id={idDesc}
                  dataSource={cnaeData as unknown as SyncfusionDataSourceItem[]}
                  fields={{ text: "Descripcion", value: "Descripcion" }}
                  placeholder={placeholder ?? "CNAE para cotización AT/EP"}
                  value={String(currentDesc ?? field.value ?? "")}
                  change={(e) => {
                    const selectedDesc = e?.value as string;
                    const selectedItem =
                      (e?.itemData as CnaeOption | undefined) ||
                      cnaeData.find((item) => item.Descripcion === selectedDesc);

                    if (selectedItem) {
                      setIfChanged(idCode, selectedItem.Codigo);
                      setIfChanged(idSelected, selectedItem.Id);
                      field.onChange(selectedItem.Descripcion);
                      return;
                    }

                    setIfChanged(idCode, "");
                    setIfChanged(idSelected, "");
                    field.onChange(selectedDesc || "");
                  }}
                  onBlur={field.onBlur}
                  enabled={!disabled}
                  ignoreCase={true}
                  ignoreAccent={true}
                  filterType="Contains"
                  minLength={0}
                />
              )}
            />
          </div>
        </div>

        <input type="hidden" {...register(idCode)} />
        <input type="hidden" {...register(idSelected)} />

        {errorMessage && <p className="error m-0">{errorMessage}</p>}
      </fieldset>
    </div>
  );
};

export default FormInputCNAE;
